/**
 * Type-to-search over requesters, with the selected client's people first.
 *
 * Every requester typeahead searches the system-wide list so a name at any
 * client can be found, but when a client is already selected its own people are
 * almost always who the user means. This ranks matches at the selected client
 * ahead of everyone else (both halves keep the API's name order) and only then
 * applies the display limit, so the client's matches are never cut off by it.
 *
 * Membership comes from the record itself (`company_id`, the full `companies`
 * list for multi-client users, `is_internal` for AIM staff when the AIM company
 * is selected) and, when the caller has the client-scoped list already loaded,
 * from that list's ids too — it is the authoritative answer for that client.
 */
export const INTERNAL_COMPANY_ID = 30;

export function requesterBelongsTo(req, companyId, clientIds = null) {
  const cid = Number(companyId);
  if (!req || !cid) return false;
  if (Number(req.company_id) === cid) return true;
  if (Array.isArray(req.companies) && req.companies.some((c) => Number(c?.id) === cid)) return true;
  if (cid === INTERNAL_COMPANY_ID && req.is_internal) return true;
  const id = Number(req.id);
  return !!clientIds && Number.isFinite(id) && clientIds.has(id);
}

/**
 * @param {Array}  list        system-wide requesters (search source)
 * @param {string} query       what the user typed (matched against name, case-insensitive)
 * @param {number|string|null} companyId  the selected client, if any
 * @param {object} [opts]
 * @param {number} [opts.limit=20]       rows to return
 * @param {Array}  [opts.clientList]     the selected client's own requester list, if loaded
 */
export function searchRequesters(list, query, companyId, opts = {}) {
  const limit = opts.limit ?? 20;
  const q = (query || '').trim().toLowerCase();
  const named = (list || []).filter((r) => r && r.name && r.name.trim() !== '');
  const matches = q ? named.filter((r) => r.name.toLowerCase().includes(q)) : named;

  if (!companyId) return matches.slice(0, limit);

  const clientIds = Array.isArray(opts.clientList) && opts.clientList.length
    ? new Set(opts.clientList.map((r) => Number(r?.id)).filter(Number.isFinite))
    : null;

  const own = [];
  const rest = [];
  for (const r of matches) {
    (requesterBelongsTo(r, companyId, clientIds) ? own : rest).push(r);
  }
  return own.concat(rest).slice(0, limit);
}
